import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyDraftOrderKey,
  ShopifyOrderKey,
  ShopifyPaymentScheduleKey,
  ShopifyPaymentScheduleToPaymentTermsKey,
  ShopifyPaymentTermsKey,
  ShopifyPaymentTermsToDraftOrderKey,
  ShopifyPaymentTermsToOrderKey,
  ShopifyPaymentTermsToShopKey,
  ShopifyShopKey,
} from "./keys";
import { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import { buildConnectionSpec, ConnectionModelSpecVersion } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyPaymentTermsKey,
  "2024-01": {
    name: "Shopify Payment Terms",
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "id", shopifyFieldKey: "id" } },
      },
      {
        name: "Due In Days",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "dueInDays", shopifyFieldKey: "due_in_days" } },
      },
      {
        name: "Order",
        fieldType: FieldType.BelongsTo,
        key: ShopifyPaymentTermsToOrderKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: { graphQLFieldKey: "orderId", shopifyFieldKey: "order_id" },
        },
        configuration: {
          relatedModelKey: ShopifyOrderKey,
        },
      },
      {
        name: "Draft Order",
        fieldType: FieldType.BelongsTo,
        key: ShopifyPaymentTermsToDraftOrderKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: { graphQLFieldKey: "draftOrderId", shopifyFieldKey: "draft_order_id" },
        },
        configuration: {
          relatedModelKey: ShopifyDraftOrderKey,
        },
      },
      {
        name: "Overdue",
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "overdue",
            graphQLOnlyField: true,
            shopifyFieldKey: "__gadget_graphql_overdue",
          },
        },
      },
      {
        name: "Payment Schedules",
        fieldType: FieldType.HasMany,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "paymentSchedules", shopifyFieldKey: "payment_schedules" } },
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifyPaymentScheduleKey,
          inverseFieldKey: ShopifyPaymentScheduleToPaymentTermsKey,
        },
      },
      {
        name: "Payment Terms Name",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "paymentTermsName", shopifyFieldKey: "payment_terms_name" } },
      },
      {
        name: "Payment Terms Type",
        fieldType: FieldType.Enum,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "paymentTermsType", shopifyFieldKey: "payment_terms_type" } },
        configuration: {
          allowOther: true,
          allowMultiple: false,
          options: [{ name: "FIXED" }, { name: "FULFILLMENT" }, { name: "NET" }, { name: "RECEIPT" }, { name: "UNKNOWN" }],
        },
      },
      {
        name: "Translated Name",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "translatedName",
            graphQLOnlyField: true,
            shopifyFieldKey: "__gadget_graphql_translated_name",
          },
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyPaymentTermsToShopKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "shop_id" } },
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: {
      // synced as part of ShopifyOrder and/or ShopifyDraftOrder
      type: "inherit",
    },
  },
  "2025-04": {
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "id",
            shopifyFieldKey: "id",
            availableInWebhook: ["payment_terms/create", "payment_terms/update"],
          },
        },
      },
      {
        name: "Due In Days",
        fieldType: FieldType.Number,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "dueInDays",
            shopifyFieldKey: "due_in_days",
            availableInWebhook: ["payment_terms/create", "payment_terms/update"],
          },
        },
      },
      {
        name: "Payment Schedules",
        fieldType: FieldType.HasMany,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "paymentSchedules",
            shopifyFieldKey: "payment_schedules",
            availableInWebhook: ["payment_terms/create", "payment_terms/update"],
          },
        },
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifyPaymentScheduleKey,
          inverseFieldKey: ShopifyPaymentScheduleToPaymentTermsKey,
        },
      },
      {
        name: "Payment Terms Name",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "paymentTermsName",
            shopifyFieldKey: "payment_terms_name",
            availableInWebhook: ["payment_terms/create", "payment_terms/update"],
          },
        },
      },
      {
        name: "Payment Terms Type",
        fieldType: FieldType.Enum,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "paymentTermsType",
            shopifyFieldKey: "payment_terms_type",
            availableInWebhook: ["payment_terms/create", "payment_terms/update"],
          },
        },
        configuration: {
          allowOther: true,
          allowMultiple: false,
          options: [{ name: "FIXED" }, { name: "FULFILLMENT" }, { name: "NET" }, { name: "RECEIPT" }, { name: "UNKNOWN" }],
        },
      },
    ],
  },
};

export const ShopifyPaymentTerms = ({
  apiVersion,
  frameworkVersionConstraint,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint?: string;
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint);
